/**
 * EU Customs Reform product identifiers (PIDs). From 1 Nov 2026 every line
 * item on a package going into the EU27 needs a merchant PID and a
 * manufacturer PID, plus the GTIN/EAN/UPC when the product has one.
 *
 * The same rules run on the server (app/booking/utils/productIdentifiers.js in
 * qcexpress-be) — the client copy exists to ask for the fields at the right
 * moment, not to be the gate. Keep the two in step.
 */

export const EU27_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE',
]

export const PID_MAX_LENGTH = 35

export type ProductIdentifiers = {
  merchant?: string
  manufacturer?: string
  standardisedManufacturer?: string
}

/** Packages to an EU27 country need PIDs; documents never do. */
export const needsProductIdentifiers = (
  receiverCountry: unknown,
  shipmentType?: unknown,
): boolean =>
  String(shipmentType ?? '').toUpperCase() !== 'DOCUMENT' &&
  EU27_COUNTRY_CODES.includes(String(receiverCountry ?? '').toUpperCase())

/** Per-field error messages for one item ('' when fine). */
export const productIdentifierErrors = (pids?: ProductIdentifiers) => {
  const check = (value: unknown, required: boolean) => {
    const v = String(value ?? '').trim()
    if (!v) return required ? 'This field is required' : ''
    return v.length > PID_MAX_LENGTH
      ? `Must be ${PID_MAX_LENGTH} characters or fewer`
      : ''
  }
  return {
    merchant: check(pids?.merchant, true),
    manufacturer: check(pids?.manufacturer, true),
    standardisedManufacturer: check(pids?.standardisedManufacturer, false),
  }
}

/**
 * The item's PIDs as DHL line-item customerReferences (ABW / MF / SRV).
 * Returns undefined when there are none, so the key is left off the line.
 */
export const toCustomerReferences = (pids?: ProductIdentifiers) => {
  const refs = [
    { typeCode: 'ABW', value: pids?.merchant },
    { typeCode: 'MF', value: pids?.manufacturer },
    { typeCode: 'SRV', value: pids?.standardisedManufacturer },
  ]
    .map((r) => ({ ...r, value: String(r.value ?? '').trim() }))
    .filter((r) => r.value)
  return refs.length ? refs : undefined
}

